/**
 * Builds of one package version on registry.pure.dev (moving-pointer
 * scheme, 2026-10-12 — src/lib/registryBuilds.ts): every pinnable
 * `X.Y.Z-pure.<compiler>` build, which one `X.Y.Z` points at, and a
 * per-build Withdraw (X.Y.Z then reverts to the previous build, or to
 * upstream's original when none is left). Withdrawing never deletes
 * anything: lockfiles that pinned a build keep installing it. Delete…
 * (single or ticked builds) is the opposite, a HARD delete from every store
 * with a dry-run listing and typed confirmation (DeleteBuildsDialog.tsx).
 */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  downloadsCell,
  downloadsTitle,
  lastSeenLabel,
  sparklineCaption,
  sparklinePoints,
  summaryFor,
  type DownloadsState,
} from "@/lib/downloads";
import {
  buildRole,
  buildRoleLabel,
  pinCommand,
  pointerSummary,
  shortIntegrity,
  type RegistryVersionBuilds,
} from "@/lib/registryBuilds";

const ROLE_VARIANT = {
  current: "success",
  available: "secondary",
  withdrawn: "outline",
  "no-pointer": "secondary",
} as const;

export function VersionBuilds({
  pkg,
  version,
  builds,
  error,
  busyBuild,
  onWithdrawBuild,
  selected,
  onToggleSelected,
  onSelectWithdrawn,
  onDeleteBuilds,
  downloads,
}: {
  pkg: string;
  version: string;
  builds: RegistryVersionBuilds | undefined;
  /** The registry's build list couldn't be read. */
  error: string | null;
  busyBuild: string | null;
  onWithdrawBuild: (buildVersion: string) => void;
  /** Builds ticked for HARD deletion (components/registry/
   *  DeleteBuildsDialog.tsx); withdraw keeps a build installable, delete
   *  does not. */
  selected: string[];
  onToggleSelected: (buildVersion: string) => void;
  onSelectWithdrawn: () => void;
  onDeleteBuilds: (buildVersions: string[]) => void;
  /** The registry's download counts for this package (lib/downloads.ts);
   *  undefined while loading, `ok: false` when unavailable. */
  downloads?: DownloadsState;
}) {
  const report = downloads?.ok ? downloads.report : null;
  if (error)
    return (
      <p className="text-xs text-muted-foreground">
        Builds on registry.pure.dev unavailable: {error}
      </p>
    );
  if (!builds || builds.builds.length === 0) return null;
  return (
    <div className="space-y-1 rounded-md border bg-muted/30 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs">
          {pointerSummary(version, builds)}
        </span>
        <span className="text-xs text-muted-foreground">
          New installs of {version} get the build it points at; lockfiles keep
          the build they recorded.
        </span>
      </div>
      {report && report.daily.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <svg
            width={120}
            height={24}
            role="img"
            aria-label={sparklineCaption(report.daily)}
          >
            <title>{sparklineCaption(report.daily)}</title>
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              points={sparklinePoints(report.daily, 120, 24)}
            />
          </svg>
          <span>
            {sparklineCaption(report.daily)} (all builds of {pkg})
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {builds.builds.some((b) => b.withdrawn) && (
          <Button variant="outline" size="sm" onClick={onSelectWithdrawn}>
            Select withdrawn
          </Button>
        )}
        {selected.length > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDeleteBuilds(selected)}
          >
            Delete {selected.length} selected…
          </Button>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="py-1 pr-2 font-medium" />
            <th className="py-1 pr-2 font-medium">Build (pinnable)</th>
            <th className="py-1 pr-2 font-medium">Compiler</th>
            <th className="py-1 pr-2 font-medium">Published</th>
            <th
              className="py-1 pr-2 font-medium"
              title="Downloads of this build's tarballs counted by registry.pure.dev: total (last 7 days / last 30 days). Installs served from a client's own cache are not seen."
            >
              Downloads
            </th>
            <th className="py-1 pr-2 font-medium">Last seen</th>
            <th className="py-1 pr-2 font-medium">Integrity</th>
            <th className="py-1 pr-2 font-medium" />
            <th className="py-1 font-medium" />
          </tr>
        </thead>
        <tbody>
          {builds.builds.map((b) => {
            const role = buildRole(builds, b);
            return (
              <tr key={b.version} className="border-t">
                <td className="py-1 pr-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${b.version} for deletion`}
                    checked={selected.includes(b.version)}
                    onChange={() => onToggleSelected(b.version)}
                  />
                </td>
                <td
                  className="py-1 pr-2 font-mono"
                  title={`Pin exactly: ${pinCommand(pkg, b.version)}`}
                >
                  {b.version}
                </td>
                <td className="py-1 pr-2 font-mono">{b.compilerVersion}</td>
                <td className="py-1 pr-2">
                  {new Date(b.publishedAt).toLocaleString()}
                </td>
                <td
                  className="py-1 pr-2 font-mono whitespace-nowrap"
                  title={
                    report
                      ? downloadsTitle(summaryFor(report, b.version))
                      : (downloads as { error?: string } | undefined)?.error
                  }
                >
                  {report ? downloadsCell(summaryFor(report, b.version)) : "—"}
                </td>
                <td className="py-1 pr-2 whitespace-nowrap">
                  {report
                    ? lastSeenLabel(summaryFor(report, b.version).lastSeen)
                    : "—"}
                </td>
                <td
                  className="py-1 pr-2 font-mono"
                  title={`build ${b.integrity}\npointer ${b.pointerIntegrity ?? "—"}`}
                >
                  {shortIntegrity(b.integrity)}
                </td>
                <td className="py-1 pr-2">
                  <Badge
                    variant={ROLE_VARIANT[role]}
                    title={
                      b.withdrawn
                        ? `Withdrawn ${new Date(b.withdrawn.at).toLocaleString()}: ${b.withdrawn.reason}`
                        : undefined
                    }
                  >
                    {buildRoleLabel(role, version)}
                  </Badge>
                </td>
                <td className="py-1 text-right space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    title="Delete this build for good: registry bytes and metadata, pipeline records and stats history are removed and the version number is retired. Lockfiles that pinned it will 404."
                    onClick={() => onDeleteBuilds([b.version])}
                  >
                    Delete…
                  </Button>
                  {!b.withdrawn && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busyBuild === b.version}
                      title={
                        role === "current"
                          ? `Withdraw this build: ${version} reverts to the previous build (or upstream's original if none is left). Nothing is deleted; lockfiles that pinned it keep installing it.`
                          : "Withdraw this build: it is marked deprecated and never pointed to again. Nothing is deleted; lockfiles that pinned it keep installing it."
                      }
                      onClick={() => onWithdrawBuild(b.version)}
                    >
                      {busyBuild === b.version ? "…" : "Withdraw"}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
